import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CoreContext } from "../../context/global/CoreContext";
import { getPayoutTimesheetDashboard } from "../../services/api/timekeeping";
import { Spinner } from "../../components/ui";
import PayrollWeekSection from "../../features/timekeeping/payoutTimesheetDashboard/PayrollWeekSection";
import {
  getMonthDateRange,
  getRecentMonthOptions,
} from "../../features/timekeeping/payoutTimesheetDashboard/summaryUtils";

const PAYOUT_DASHBOARD_REFRESH_CHANNEL = "payout-timesheet-dashboard-refresh";
const PAYOUT_DASHBOARD_REFRESH_EVENT = "payout-timesheet-saved";

/**
 * PayoutTimesheetDashboard renders pending payout rows and historical weekly payroll summaries.
 *
 * @returns {JSX.Element} Payout timesheet dashboard page.
 */
const PayoutTimesheetDashboard = () => {
  const { user, appName } = useContext(CoreContext);
  const monthOptions = useMemo(() => getRecentMonthOptions(12), []);
  const [selectedMonthValue, setSelectedMonthValue] = useState(monthOptions[0]?.value || "");
  const [dashboardData, setDashboardData] = useState({
    weeks: [],
    currentWeekEnding: null,
  });
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeNoShiftTimesheets, setIncludeNoShiftTimesheets] = useState(true);
  const visibleWeeks = useMemo(() => {
    if (includeNoShiftTimesheets) {
      return dashboardData.weeks;
    }

    return dashboardData.weeks.map((week) => {
      const timesheets = (week.timesheets || []).filter(
        (timesheet) => Number(timesheet.shiftCount) !== 0
      );
      const projectIds = new Set(
        timesheets.flatMap((timesheet) => timesheet.projectIds || [])
      );

      return {
        ...week,
        timesheets,
        summary: {
          ...week.summary,
          timesheetCount: timesheets.length,
          totalWorkerHours: timesheets.reduce((total, row) => total + Number(row.workerHours || 0), 0),
          totalHoursLogHours: timesheets.reduce((total, row) => total + Number(row.hoursLogHours || 0), 0),
          totalPayoutHours: timesheets.reduce((total, row) => total + Number(row.payoutHours || 0), 0),
          totalShifts: timesheets.reduce((total, row) => total + Number(row.shiftCount || 0), 0),
          totalProjects: projectIds.size,
          pendingCount: timesheets.filter((row) => row.status === "pending").length,
          approvingCount: timesheets.filter((row) => row.status === "approving").length,
          approvedCount: timesheets.filter((row) => row.status === "approved").length,
          exportedCount: timesheets.filter((row) => row.status === "exported").length,
          editedCount: timesheets.filter((row) => row.status === "edited").length,
        },
      };
    });
  }, [dashboardData.weeks, includeNoShiftTimesheets]);

  const fetchDashboard = useCallback(
    async ({ resetExpandedWeeks = false, silent = false } = {}) => {
      if (!selectedMonthValue) {
        return;
      }

      if (!silent) {
        setIsLoading(true);
      }
      setError("");
      try {
        const range = getMonthDateRange(selectedMonthValue);
        const response = await getPayoutTimesheetDashboard(range);
        const weeks = response.weeks || [];
        setDashboardData({
          weeks,
          currentWeekEnding: response.currentWeekEnding || null,
        });
        if (resetExpandedWeeks) {
          setExpandedWeeks(
            new Set(
              weeks
                .filter((week) => !week.statuses?.iifExported)
                .map((week) => week.weekEnding)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching payout dashboard:", error);
        setError("Unable to load payout dashboard data.");
      } finally {
        setIsLoading(false);
      }
    },
    [selectedMonthValue]
  );

  useEffect(() => {
    fetchDashboard({ resetExpandedWeeks: true });
  }, [fetchDashboard]);

  useEffect(() => {
    const supportsBroadcastChannel = typeof window !== "undefined" &&
      "BroadcastChannel" in window;
    const refreshDashboard = () => fetchDashboard({ silent: true });

    if (supportsBroadcastChannel) {
      const channel = new BroadcastChannel(PAYOUT_DASHBOARD_REFRESH_CHANNEL);
      channel.onmessage = (event) => {
        if (event.data?.type === PAYOUT_DASHBOARD_REFRESH_EVENT) {
          refreshDashboard();
        }
      };
      return () => channel.close();
    }

    const handleStorage = (event) => {
      if (event.key === PAYOUT_DASHBOARD_REFRESH_CHANNEL && event.newValue) {
        refreshDashboard();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [fetchDashboard]);

  const handleTimesheetSelect = (workerID, weekEnding) => {
    // Open the payout timesheet in a new tab.
    window.open(
      `/app/timekeeping/payout-timesheet/${workerID}/${weekEnding}`,
      "_blank"
    );
  };

  const handleToggleWeek = (weekEnding) => {
    setExpandedWeeks((current) => {
      const next = new Set(current);
      if (next.has(weekEnding)) {
        next.delete(weekEnding);
      } else {
        next.add(weekEnding);
      }
      return next;
    });
  };

  React.useEffect(() => {
    document.title = `Timesheet Payout Review`;
    if (appName) {
      appName.current = "Timesheet Payout Review";
    }
  }, [user, appName]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-custom-rsbg">
        <Spinner size="h-12 w-12" className="text-green-900" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-custom-rsbg font-inter text-gray-900">
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(22,163,74,0.1),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(10,42,13,0.1),_transparent_34%)]" />

        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-3 py-4 sm:px-5 lg:px-6">
          <header className="flex items-center justify-between gap-3 rounded-lg border border-white/70 bg-white/90 px-4 py-3 shadow shadow-primary-900/10">
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-xl font-bold tracking-tight text-gray-950">
                Timesheet Payout Review
              </h1>
            </div>

            <div className="flex flex-shrink-0 flex-col items-end gap-2">
              <select
                className="min-h-[28px] rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
                value={selectedMonthValue}
                onChange={(event) => setSelectedMonthValue(event.target.value)}
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300 text-green-900 focus:ring-green-900"
                  checked={includeNoShiftTimesheets}
                  onChange={(event) => setIncludeNoShiftTimesheets(event.target.checked)}
                />
                Include Timesheets with no Shifts
              </label>
            </div>
          </header>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          ) : null}

          <section className="space-y-2">
            {visibleWeeks.length === 0 ? (
              <div className="rounded-lg border border-white/70 bg-white px-4 py-6 text-center text-sm text-gray-500 shadow shadow-primary-900/10">
                No payout timesheets found for this month.
              </div>
            ) : (
              <div className="space-y-2">
                {visibleWeeks.map((week) => (
                  <PayrollWeekSection
                    key={week.weekEnding}
                    week={week}
                    isExpanded={expandedWeeks.has(week.weekEnding)}
                    onToggle={handleToggleWeek}
                    onSelect={handleTimesheetSelect}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default PayoutTimesheetDashboard;
